import { Api } from "../api";
import { AxiosResponse, CancelTokenSource } from "axios";

/**
 * @typedef {{
 *  id: number,
 *  municipalityInternalId: string,
 *  category: string,
 *  title: boolean,
 *  projectStatus: string,
 *  reportStatus: string }} Project
 */

/**
 * @typedef {{
 *  id: number,
 *  fieldCode: string,
 *  updatedByName: string,
 *  updatedByEmail: string,
 *  message: string }} ReviewFlag
 */

/**
 * @typedef {{
 *  id: number,
 *  isPrimary: boolean,
 *  latitude: number,
 *  longitude: number }} ProjectLocation
 */

/**
 * @typedef {{
 *  id: number,
 *  name: string }} File
 */

/**
 * @typedef {{
 *  year: number,
 *  amount: number }} ProjectFund
 */

/**
 * @typedef {{
 *  total: number,
 *  funds: ProjectFund[] }} ProjectFunds
 */

/**
 * @typedef {{
 *  id: number,
 *  name: string,
 *  subcategories: ProjectCategory[] }} ProjectCategory
 */

/**
 * @typedef {{
 *  id: number,
 *  municipalityId: number,
 *  municipalityName: string,
 *  title: string,
 *  projectCost: number,
 *  ccbfFunding: number,
 *  categoryId: number,
 *  categoryCode: string,
 *  categoryName: string,
 *  assigneeName: string,
 *  statusId: number,
 *  statusName: string,
 *  deletionRequestedName: string,
 *  requestDate: Date}} ProjectReview
 */

/**
 * @typedef {{
 *  id: number,
 *  categoryId: number,
 *  subcategoryId: number,
 *  categoryChangeRequested: boolean,
 *  flags: ReviewFlag[] }} ProjectCategoryData
 */

/**
 * @typedef {{
 *  id: number,
 *  ccbfFunding: number,
 *  totalCcbfBudgeted: number,
 *  totalProjectCost: number,
 *  endOfFinancingDate: number,
 *  additionalComments: string,
 *  flags: ReviewFlag[] }} ProjectFinancialsData
 */

/**
 * @typedef {{
 *  id: number,
 *  title: string,
 *  internalId: string,
 *  objectives: string,
 *  scopeOfWork: string,
 *  startDate: Date,
 *  constructionEndDate: Date,
 *  investmentTypeId: number,
 *  signagePosted: boolean,
 *  additionalComments: string,
 *  communicationsActivities: string,
 *  locations: ProjectLocation[],
 *  files: File[],
 *  flags: ReviewFlag[] }} ProjectInformationData
 */

/**
 * @typedef {{
 *  endOfConstructionDate: Date,
 *  endOfFinancingDate: Date,
 *  closingBalance: number,
 *  projectDeletionRequested: boolean
 * }} ProjectValidationInfoResponse
 */

/**
 * @typedef {{
 *  flagId: number,
 *  ownerName: string,
 *  lastUpdate: string,
 *  comment: string
 * }} FlagProps
 */

/**
 * @typedef {{
 *  value: string,
 *  previousValue: string,
 *  flagProps: FlagProps
 * }} ReviewFieldValue
 */

/**
 * @typedef {{
 *  projectId: number,
 *  statusId: number,
 *  communicatioStatusId: number,
 *  municipalityId: number,
 *  assigneeId: number,
 *  category: {
 *    selectedCategory: ReviewFieldValue,
 *    selectedSubcategory: ReviewFieldValue
 *  },
 *  projectDetails: {
 *    title: ReviewFieldValue,
 *    objectives: ReviewFieldValue,
 *    scopeOfWork: ReviewFieldValue,
 *    natureOfInvestment: ReviewFieldValue,
 *    additionalComments: ReviewFieldValue
 *  },
 *  location: {
 *    primaryLocation: ReviewFieldValue
 *  },
 *  dates: {
 *    startDate: ReviewFieldValue,
 *    constructionEndDate: ReviewFieldValue,
 *    financingEndDate: ReviewFieldValue
 *  },
 *  financials: {
 *    ccbfFundsApplied: ProjectFund[],
 *    totalFundsBudgeted: ReviewFieldValue,
 *    totalCost: ReviewFieldValue
 *  },
 *  communications: {
 *    signagePosted: ReviewFieldValue,
 *    fileUpload: File[]
 *  }
 * }} ProjectReviewData
 */

/**
 * @typedef {{
 *  municipalityId: number,
 *  municipalityName: string,
 *  projectId: number,
 *  title: string,
 *  objectives: string,
 *  totalCost: number,
 *  category: string,
 *  indicatorType: string,
 *  indicatorDescription: string,
 *  textValue: string,
 *  numericValue: number,
 *  beforeValue: number,
 *  afterValue: number,
 *  absoluteChange: number,
 *  unitCost: number,
 *  unitName: string }} ResultsReportingLog
 */

/**
 * @typedef {{
 *  id: number,
 *  actionTypeId: number,
 *  actionDescription: string,
 *  actionDate: string,
 *  actionBy: string }} HistoryLog
 */

/**
 * @typedef {{
 *  id: number,
 *  signagePosted: boolean,
 *  communicationActivities: string,
 *  files: File[],
 *  flags: ReviewFlag[]}} CommunicationsData
 */

/**
 * @typedef {{
 *  id: number,
 *  municipalityInternalId: string,
 *  municipalityId: number,
 *  municipalityName: string,
 *  title: string,
 *  scopeOfWork: string,
 *  objectives: string,
 *  additionalComments: string,
 *  category: string,
 *  subcategory: string,
 *  investmentType: string,
 *  latitude: number,
 *  longitude: number,
 *  startDate: Date,
 *  constructionEndDate: Date,
 *  financingEndDate: Date,
 *  totalCost: number,
 *  ccbfFundsSum: number,
 *  totalBudgeted: number,
 *  signagePosted: boolean,
 *  fileUploaded: boolean,
 *  communicationsActivities: string,
 *  reviewStatus: string,
 *  reviewAssignee: string }} AmoQueryProject
 */

/**
 * @typedef {{
 *  id: number,
 *  reportingYear: number,
 *  fundingApplied: number,
 *  category: string }} AmoQueryProjectExpenditure
 */

/**
 * @typedef {{
 *  projectId: number,
 *  reason: string }} MunicipalDeletionRequest
 */

const api = new Api("api/projects");

/**
 * @returns {Promise<AxiosResponse<Project[]>>}
 */
export const getAll = () => api.get();

/**
 * @returns {Promise<AxiosResponse<ProjectCategory[]>>}
 */
export const getAllCategories = () => api.get("categories");

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<ProjectReview[]>>}
 */
export const getReviewByYear = (year) => api.get(`review/${year}`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectReview>>}
 */
export const getProjectReviewById = (id) => api.get(`${id}/review`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectFunds>>}
 */
export const getFunds = (id) => api.get(`${id}/funds`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectCategoryData>>}
 */
export const getCategoryData = (id) => api.get(`${id}/category`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectInformationData>>}
 */
export const getInformationData = (id) => api.get(`${id}/information`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectFinancialsData>>}
 */
export const getFinancialsData = (id) => api.get(`${id}/financials`);

export const create = (payload) => api.post("category", payload);

export const changeCategory = (id, payload) =>
  api.post(`${id}/category`, payload);

export const updateInformation = (id, payload) =>
  api.put(`${id}/information`, payload);

export const updateFinancials = (id, payload) =>
  api.put(`${id}/financials`, payload);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse>}
 */
export const deleteProject = (id) => api.del(`${id}`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse>}
 */
export const deleteProjectMunicipal = (id) => api.del(`municipal/${id}`);

/**
 * @param {MunicipalDeletionRequest} payload
 * @returns {Promise<AxiosResponse>}
 */
export const deleteRequestProjectMunicipal = (payload) =>
  api.post(`deletion/request`, payload);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse>}
 */

export const deleteProjectAMO = (id) => api.del(`${id}/true`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse>}
 */
export const denyDeleteProjectAMO = (id) => api.del(`${id}/false`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<ProjectValidationInfoResponse>>}
 */
export const getValidationInfo = (id) => api.get(`${id}/validation-info`);

/**
 * @param {number[]} projectIds
 * @returns {Promise<AxiosResponse<Project[]>>}
 */
export const downloadTemplate = (projectIds) =>
  api.post("template", projectIds);

/**
 * @param {number} year
 * @returns {Promise<AxiosResponse<ResultsReportingLog[]>>}
 */
export const getResultsReportingLog = (year) =>
  api.get(`review/results/${year}`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<HistoryLog>>}
 */
export const getCommunicationsHistory = (id) =>
  api.get(`${id}/communications-history`);

/**
 * @param {number} id
 * @returns {Promise<AxiosResponse<CommunicationsData>>}
 */
export const getCommunicationsData = (id) => api.get(`${id}/communication`);

export const updateCommunications = (id, payload) =>
  api.put(`${id}/communication`, payload);

/**
 * @param {number} projectId
 * @returns {Promise<AxiosResponse<any>>}
 */
export const getProjectChanges = (projectId) => api.get(`${projectId}/changes`);

/**
 * @param {CancelTokenSource} source
 * @returns {Promise<AxiosResponse<AmoQueryProject[]>>}
 */
export const getAllProjectsForExport = (source) =>
  api.get("export", undefined, { cancelToken: source.token });

/**
 * @param {CancelTokenSource} source
 * @returns {Promise<AxiosResponse<AmoQueryProjectExpenditure[]>>}
 */
export const getAllProjectsExpendituresForExport = (source) =>
  api.get("export/expenditures", undefined, {
    cancelToken: source.token,
  });
